import type { BIRequest } from '@/entities/bi'
import type { ColumnConfig } from '@/entities/data-export'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	SRT_ColumnDef,
	SRT_RowData,
} from '@/shared/components/master-table'
import type { BIResponse } from '@/shared/generated'
import type { TFunction } from 'i18next'
import type { WidgetPeriods } from '../types'

import { useMemo, useState } from 'react'
import { isMoneyMetric, useMoneyMetrics } from '@/pages/dashboard/lib/money'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { useWidgetTable } from '@/pages/dashboard/lib/useWidgetTable'
import { any, equals, find, isNil, orderBy, pipe, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { comparePeriod, useBIAnalyticQuery, zipSeries } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import { ColumnDataType } from '@/shared/components/data-grid/types'
import {
	createSRTColumnHelper,
	MasterTable,
	SRT_TableToolbar,
} from '@/shared/components/master-table'
import { ValueType } from '@/shared/components/query-builder'
import { ampli } from '@/shared/lib/ampli'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import MetricPerfomanceHeader from '../../MetricPerfomanceHeader'

const metrics = [
	'sales_items',
	'revenue',
	'gross_profit',
	'gross_profit_margin',
]
export type DataRow = {
	brand: string
	sales_items: number
	revenue: number
	gross_profit: number
	gross_profit_margin: number
}
type AnalyticsResponse = Omit<BIResponse, 'data'> & { data: DataRow[] }
const getRowId = prop('brand')

function comparePeriods(
	mainZipped: DataRow[] = [],
	comparedZipped: DataRow[] = [],
	meta: string[] = [],
	getUniqKey: (i: DataRow) => string
): DataRow[] {
	if (!mainZipped || !comparedZipped) {
		return []
	}
	const output: DataRow[] = []
	for (let i = 0; i < mainZipped.length; i++) {
		const current = mainZipped[i]
		const prev = find(
			pipe([getUniqKey, equals(getUniqKey(current))]),
			comparedZipped
		) as DataRow
		output.push(comparePeriod<DataRow>(mainZipped[i], prev, meta))
	}

	return output
}
const getReportSchema = <T extends SRT_RowData>(
	columns: SRT_ColumnDef<T, any>[]
): ColumnConfig[] => [
	...columns.map(({ id, header }) => ({
		key: id === 'name' ? 'brand' : id!,
		type: id === 'name' ? ValueType.string : ValueType.number,
		name: header,
		custom_name: header,
		static_value: null,
		reference: null,
		enabled: true,
	})),
]
const getExportName = (name: string, periods: WidgetPeriods): string =>
	`${name} ${periods.main.start_date}-${periods.main.end_date} -> ${periods.compared.start_date}-${
		periods.compared.end_date
	}`
export default function MetricsByBrand({
	commonPeriods,
	commonFilters,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const [localPeriods, setLocalPeriods] = useState<WidgetPeriods | null>(null)
	const defaultPeriods = commonPeriods
	const currency = useSelectedCurrency()
	const moneyMetrics = useMoneyMetrics()
	const commonQueryParams = {
		currency,
		label: 'MetricsByBrand',
		metrics,
		dimensions: ['brand'] as BIRequest['dimensions'],
		date_aggregation: null,
		filters: [...commonFilters, ...(params.filters ?? [])],
	}
	const select = (data: BIResponse | null): AnalyticsResponse => ({
		...data!,
		data: data
			? zipSeries<DataRow>(data?.meta, orderBy([0], ['asc'], data?.data))
			: [],
	})
	const mainPeriodQuery = useBIAnalyticQuery<AnalyticsResponse>(
		{
			...(localPeriods ? localPeriods?.main : commonPeriods.main),
			...commonQueryParams,
		},
		{
			select,
		}
	)
	const comparisonPeriodQuery = useBIAnalyticQuery<AnalyticsResponse>(
		{
			...(localPeriods ? localPeriods?.compared : commonPeriods.compared),
			...commonQueryParams,
		},
		{
			select,
		}
	)
	const isLoading = any(prop('isLoading'), [
		mainPeriodQuery,
		comparisonPeriodQuery,
	])
	const columns = useMemo(
		() => getColumns(t, currency, moneyMetrics),
		[t, currency, moneyMetrics]
	)
	const fields = getReportSchema<DataRow>(columns)
	const rows = comparePeriods(
		mainPeriodQuery?.data?.data,
		comparisonPeriodQuery?.data?.data,
		mainPeriodQuery?.data?.meta,
		getRowId
	)
	const name = getExportName(
		t('widget.metric_by_brand.title'),
		localPeriods || commonPeriods
	)
	const handleFilterChange = (filters: FilterSchema[]) => {
		ampli.dashboardBiApplyFilterMetricsByBrand({
			'filter by': filters.map(prop('name')).join(','),
		})
		onChangeParams({ ...params, filters })
	}
	const table = useWidgetTable<DataRow>({
		rows,
		columns,
		isLoading,
		getRowId,
		getRowHeight: () => 30,
	})

	return (
		<div className='flex flex-auto flex-col gap-3'>
			<h4>{t('widget.metric_by_brand.title')}</h4>
			<div className='flex w-full items-center gap-2'>
				<SRT_TableToolbar table={table} />
				<MetricPerfomanceHeader
					data={{ fields, rows, name, kind: 'metric_by_brand' }}
					params={params}
					commonFilters={commonFilters}
					onFilterChange={handleFilterChange}
					periods={localPeriods || commonPeriods}
					defaultPeriods={defaultPeriods}
					setPeriods={setLocalPeriods}
				/>
			</div>
			<div className='border-base relative flex h-96 max-h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
				<MasterTable table={table} />
			</div>
		</div>
	)
}
const columnHelper = createSRTColumnHelper<DataRow>()

const getTextColor = (value: number) => {
	if (value > 0) {
		return 'text-success'
	}
	if (value < 0) {
		return 'text-destructive'
	}
}

function getColumns(
	t: TFunction,
	currency: string | undefined,
	moneyMetrics: string[]
) {
	return [
		columnHelper.accessor((row) => row?.brand || 'n/a', {
			id: 'name',
			header: t('brand', { defaultValue: 'Brand' }),
			enableSorting: true,
			size: 290,
			meta: {
				label: t('brand', { defaultValue: 'Brand' }),
				placeholder: t('general_search', { defaultValue: 'Search by brand' }),
				variant: 'text',
			},
		}),
		...metrics.reduce(
			(cols, metric) => {
				cols.push(
					columnHelper.accessor(prop(metric) as (row: DataRow) => number, {
						id: metric,
						header: t(metric),
						Cell: ({ cellValue }) =>
							isMoneyMetric(metric, moneyMetrics) && !isNil(cellValue) ? (
								<div className='cell-data-number'>
									{formatMoney(cellValue, currency, NumberFormats.Fixed)}
								</div>
							) : (
								<NumberField
									value={cellValue}
									schema={{ type: ColumnDataType.Float }}
								/>
							),
						size: 125,
					})
				)

				cols.push(
					columnHelper.accessor(
						prop(metric + '_difference') as (row: DataRow) => number,
						{
							id: metric + '_difference',
							header: t('field_schema_delta_abs', {
								ns: 'fields',
								defaultValue: 'Δ',
							}),
							Cell: ({ cellValue }) => (
								<div className='flex items-center justify-end font-mono'>
									<p className={getTextColor(cellValue!)}>
										{isNil(cellValue) && cellValue != 0
											? '-'
											: isMoneyMetric(metric, moneyMetrics)
												? formatMoney(cellValue, currency, NumberFormats.Fixed)
												: formatNumber(cellValue)}
									</p>
								</div>
							),
							size: 125,
						}
					)
				)
				cols.push(
					columnHelper.accessor(
						prop(metric + '_difference_percent') as (row: DataRow) => number,
						{
							id: metric + '_difference_percent',
							header: t('field_schema_delta_pp', {
								ns: 'fields',
								defaultValue: 'Δ %',
							}),
							Cell: ({ cellValue }) => (
								<div className='flex items-center justify-end font-mono'>
									<p className={getTextColor(cellValue!)}>
										{isNil(cellValue) && cellValue != 0
											? '-'
											: formatNumber(cellValue, NumberFormats.Percent)}
									</p>
								</div>
							),
							size: 125,
						}
					)
				)

				return cols
			},
			[] as SRT_ColumnDef<DataRow, number>[]
		),
	]
}
